import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarQuickFilter
} from "@mui/x-data-grid";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PulseLoader } from "react-spinners";
import { toast } from "sonner";
import axiosInstance from "../Axios.jsx";
import useAuth from "../contexts/UserContext.tsx";
import { DeleteUserModal } from "./DeleteUserModal.jsx";
import { EditDashboardUserModal } from "./EditDasboardUserModal.jsx";
import { EditIndividualDashboardUserModal } from "./EditIndividualDashboardUserModal.jsx";

function CustomToolbar() {
  return (
    <GridToolbarContainer
      sx={{ padding: 2, display: "flex", justifyContent: "space-between" }}
    >
      <GridToolbarQuickFilter />
      <div className="flex">
        <Button
          className="mx-4"
          variant="contained"
          sx={{
            backgroundColor: "#4CAF50",
            color: "white",
            "&:hover": {
              backgroundColor: "#45a049",
            },
          }}
        >
          <Link
            style={{ textDecoration: "none" }}
            to="/add-dashboard-user"
            className="hover:text-white font-medium"
          >
            Add new user
          </Link>
        </Button>
        <GridToolbarExport printOptions={{ disableToolbarButton: true }} />
      </div>
    </GridToolbarContainer>
  );
}

const DashboardUsersTable = ({ data, loading }) => {
  const { user } = useAuth();
  const [districts, setDistricts] = useState([]);

  data = data.map((user) => {
    return {
      ...user,
      user_id: parseInt(user.user_id),
    };
  });

  const columns = [
    { field: "user_id", headerName: "ID", width: 70 },
    { field: "user_fname", headerName: "First Name", width: 130 },
    { field: "user_lname", headerName: "Last Name", width: 130 },
    {
      field: "view_access_list",
      headerName: "Access List",
      flex: 1,
      autoHeight: true,
      renderCell: (params) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          {params.value}
        </div>
      ),
    },
  ];

  const getDistricts = useCallback(async () => {
    axiosInstance
      .get(`getDistricts?auth_token=${user.auth_token}`)
      .then((res) => {
        let districtsArray = res.data.data.districts.map(
          (district) => district.dist_name
        );
        setDistricts(districtsArray);
      })
      .catch((err) => {
        toast.error(err);
      });
  }, [user.auth_token]);

  if (user.user_type === "admin") {
    columns.push({
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => {
        try {
          const view_access_list = JSON.parse(params.row.view_access_list);
          const isIndividualSchool = view_access_list.isIndividualSchool;
          return (
            <div className="flex gap-2">
              {isIndividualSchool ? (
                <EditIndividualDashboardUserModal
                  userDetails={params.row}
                  districts={districts}
                />
              ) : (
                <EditDashboardUserModal userDetails={params.row} />
              )}
              <DeleteUserModal userDetails={params.row} />
            </div>
          );
        } catch {
          console.error(params.row);
          toast.error("Error parsing view access list");
        }
      },
    });
  }

  useEffect(() => {
    getDistricts();
  }, [getDistricts]);

  return (
    <TableContainer
      component={Paper}
      className="tableContainer"
      sx={{ boxShadow: "2px 4px 10px 1px rgba(201, 201, 201, 0.47)" }}
    >
      <div className="flex justify-between items-center">
        <span className="font-bold ml-2">Dashboard Users</span>
      </div>
      {loading && (
        <div className="flex justify-center items-center h-[50vh]">
          <PulseLoader color="#3B82F6" loading={loading} size={15} />
        </div>
      )}
      {!loading && (
        <DataGrid
          sx={{
            marginTop: "2em",
            "& .MuiDataGrid-cell:focus": {
              border: "none",
              outline: "none",
            },
          }}
          columns={columns}
          rows={data}
          rowHeight={50}
          pageSize={10}
          rowsPerPageOptions={[10]}
          pagination
          disablePageSize
          disableSelectionOnClick
          autoHeight={true}
          getRowId={(row) => row.user_id}
          getRowHeight={(params) => {
            const padding = 16; // Extra padding to add to the row height
            const lineHeight = 10; // Approximate line height in pixels
            const minHeight = 52; // Default row height
            const lineCount = Math.ceil(
              params.model.view_access_list.length / 40
            );
            const height = lineCount * lineHeight + padding;
            return Math.max(minHeight, height);
          }}
          components={{
            Toolbar: CustomToolbar,
          }}
          componentsProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
        />
      )}
    </TableContainer>
  );
};

export default DashboardUsersTable;
